/**
 * openQueryResults.js — the shared "open a saved query in the rate-results
 * view" mappings.
 *
 * These builders turn a saved query document into the exact payload each
 * results page expects. They were previously inlined in
 * `Queries.jsx > handleSeachFormdata`, which was the only way to reach either
 * results page: the page had to be *pushed* the data (redux + router state)
 * immediately before navigating. The quotation email now deep-links straight
 * to those pages (`?queryId=<id>`), so the results pages bootstrap themselves
 * with the very same mappings — hence one shared definition here instead of
 * two copies drifting apart.
 *
 * `mapBookingSearchQuery` lives here (rather than in NewSearchRates.jsx, which
 * still re-exports it for its existing importers) purely so importing it can't
 * drag a heavy page module — and its whole component tree — into the results
 * bundles.
 */

// The saved query returned by `single_booking_data` uses a quote-style shape
// (originPort / destinationPort, goodsDetails.commodity_description, empty
// packagingDetails, ...). The ocean search form's prefill instead reads
// originShipment / destinationShipment / commonditytype. Map one to the other
// so every field auto-populates — and so the form doesn't crash reading the
// keys it expects (searchquery.originShipment.originShipment).
// A saved query's boolean fields can round-trip through the API as the
// STRING "false" rather than a real boolean — `!!"false"` is true, which
// would silently flip a toggle back on. Coerce explicitly instead of relying
// on truthiness.
const toBool = (v, fallback) =>
    v == null ? fallback : v === false || v === 'false' || v === 0 || v === '0' ? false : !!v

export const mapBookingSearchQuery = (raw) => {
    if (!raw) return null
    const op = raw.originPort || raw.originShipment || {}
    const dp = raw.destinationPort || raw.destinationShipment || {}
    const gd = raw.goodsDetails || {}
    // containerDetails is either a flat array (ocean-submit shape) or nested
    // under containerDetails.containerDetails (single_booking_data shape).
    const containers = Array.isArray(raw.containerDetails)
        ? raw.containerDetails
        : Array.isArray(raw.containerDetails?.containerDetails)
        ? raw.containerDetails.containerDetails
        : []
    // Cargo-ready date can arrive as DD/MM/YYYY; the form parses DD-MM-YYYY.
    const cargoreadydate = (gd.cargoreadydate || '').replace(/\//g, '-')
    return {
        cargoType: 'Ocean Cargo',
        // The query's own owner — needed so a KAM re-searching from here (via
        // Edit Query) can resolve customerId server-side requires (see
        // ShipmentRateForm's onSubmit) without this, a KAM re-search from this
        // page would fail with "customerId is required..." since editQuery
        // would have no owner to fall back on.
        userId: raw.userId || '',
        containerLoad: raw.containerLoad || 'FCL',
        originType: op.originLocation ? 'DOOR' : (raw.originType || 'PORT'),
        // Preserve the Local Charges toggle the original search was run
        // with — omitted raw values default to on (matches the form's own
        // default), not off.
        originLocalCharges: toBool(raw.originLocalCharges, true),
        originShipment: {
            originShipment: op.originPort || op.originShipment || '',
            originLocation: op.originLocation || '',
            originPickup: op.originPickup || false,
            originLocationMarker: op.originLocationMarker || {},
            originPortMarker: op.originPortMarker || {},
            originLocationCountry: op.originLocationCountry || '',
        },
        destinationType: dp.destinationLocation ? 'DOOR' : (raw.destinationType || 'PORT'),
        destinationLocalCharges: toBool(raw.destinationLocalCharges, true),
        destinationShipment: {
            destinationShipment: dp.destinationPort || dp.destinationShipment || '',
            destinationLocation: dp.destinationLocation || '',
            destinationPickup: dp.destinationPickup || false,
            destinationLocationMarker: dp.destinationLocationMarker || {},
            destinationPortMarker: dp.destinationPortMarker || {},
            destinationLocationCountry: dp.destinationLocationCountry || '',
        },
        containerDetails: containers,
        // Every ocean shipment has at least one master B/L — a query record
        // that never stored a count reads as 1, not blank. The count is
        // persisted as a string, so compare numerically: a stored "0" is
        // truthy and would rehydrate the form with a zero count.
        Noofbl: Number(raw.Noofbl) > 0 ? Number(raw.Noofbl) : 1,
        commonditytype: gd.commodity_description || raw.commonditytype || '',
        stuffing: gd.stuffing ?? raw.stuffing ?? '',
        goodsDetails: { cargoreadydate },
        paymentTerms: {
            paymentType: raw.paymentTerms?.paymentType || '',
            incoTerms: raw.paymentTerms?.incoTerms || '',
        },
        platform: Array.isArray(raw.platform) ? raw.platform : [],
        online: Array.isArray(raw.online) ? raw.online : [],
        currency: raw.currency || '',
    }
}

/**
 * Ocean: the `all-ocean-rates` payload for a saved query.
 * `requestType: 1` = re-quote this same query (not a new one).
 *
 * `manualQuoteOnly` is the ocean mirror of the air payload's flag of the same
 * name (see Newratelisting's `combinednewpayload`): the query was already
 * quoted by hand, so there is nothing to gain from re-running the Freightify
 * fan-out — the backend can answer from the saved quote alone. Air additionally
 * narrows its socket subscription to MQ_res; ocean has no socket layer (one
 * request/response), so the flag is the whole mechanism here.
 */
export const buildOceanSearchQuery = (doc, { manualQuoteOnly = false } = {}) => ({
    ...mapBookingSearchQuery(doc),
    queryId: doc?._id,
    requestType: 1,
    // Quote_status only when the document actually carries it — the flag is also
    // sent for the email deep-link, whose projection may not include it, and
    // asserting "Manual Quoted" there would be inventing a status. The endpoint
    // decides off the stored quote anyway; this is context, not instruction.
    ...(manualQuoteOnly
        ? {
              manualQuoteOnly: true,
              ...(doc?.Quote_status ? { Quote_status: doc.Quote_status } : {}),
          }
        : {}),
})

/**
 * Ocean: the router state OceanMainRateCom reads (`state.data` drives the
 * query bar + Edit-Query prefill; commondity/stuffing feed the booking body).
 */
export const buildOceanResultsState = (oceanSearchquery) => ({
    data: oceanSearchquery,
    commondity: oceanSearchquery?.commonditytype || '',
    stuffing: oceanSearchquery?.stuffing || '',
})

/**
 * Air: the `nodeData` shape Newratelisting reads and forwards to
 * /ratesearch/all. Accepts either a booking-history row projection or the
 * full Query document (`ratesearch/get-query`) — both carry these fields.
 */
export const buildAirSearchPayload = (doc) => ({
    searchquery: {
        cargoType: doc?.cargoType || 'Air Cargo',
        originPort: doc?.originPort,
        destinationPort: doc?.destinationPort,
        goodsDetails: doc?.goodsDetails,
        packagingDetails: {
            ...doc?.packagingDetails,
            packagingType: {
                ...doc?.packagingDetails?.packagingType,
                weightMode: doc?.packagingDetails?.packagingType?.weightMode ?? 1,
            },
        },
        paymentTerms: doc?.paymentTerms,
        _id: doc?._id,
        // Carry the query's status (e.g. "Manual Quoted") into the
        // rate-search payload so the results flow knows these are
        // manual-quote rates.
        Quote_status: doc?.Quote_status,
        // IATA CASS code from the booking-history row. The results page
        // (Newratelisting) reads `searchquery.cass` into the `iataCass`
        // field of the /ratesearch/all payload; also expose `iataCass`
        // directly for any consumer that reads that key.
        cass: doc?.iataCass,
        iataCass: doc?.iataCass,
        // Carry the manual-quote expiry through to the results page so it
        // gets included in the /ratesearch/all payload. Field name varies
        // by endpoint, so check the known variants (same as the row's
        // expiry resolution in the table).
        quoteExpiry:
            doc?.quoteExpiry ??
            doc?.queryExpiryDate ??
            doc?.nodeData?.result?.queryExpiryDate ??
            null,
    },
    requestType: '1',
    apiCall: 'WEB',
    isManualQuoted: doc?.Quote_status === 'Manual Quoted',
})
